import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Eye, Cookie, Share2, UserCheck, Mail, Globe, Settings } from 'lucide-react';
import { trackPageView } from '../analytics';

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-8">
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon className="w-6 h-6 text-blue-600" />}
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    </div>
    <div className="text-gray-700 space-y-3 pl-9">
      {children}
    </div>
  </div>
);

const PrivacyPolicyPage = () => {
  useEffect(() => {
    trackPageView('privacy_policy');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-500 mt-2">Last updated: December 2024</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8">
          <div className="mb-8">
            <p className="text-gray-700">
              CoinCommons ("we", "us", or "our") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, and safeguard your information
              when you use our platform.
            </p>
          </div>

          <Section icon={Database} title="Information We Collect">
            <p><strong>Information you provide:</strong></p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Wallet Address:</strong> When you sign in with your wallet,
                we store your public wallet address. We never have access to your
                private keys or funds.
              </li>
              <li>
                <strong>Email Address:</strong> If you sign in with email, we store
                your email address for authentication purposes.
              </li>
              <li>
                <strong>User Content:</strong> Comments, votes, aliases, and other
                content you submit to the platform.
              </li>
            </ul>
            <p className="mt-3"><strong>Information collected automatically:</strong></p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Device information (browser type, operating system)</li>
              <li>IP address</li>
              <li>Usage data (pages visited, features used)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </Section>

          <Section icon={Eye} title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Provide and maintain our services</li>
              <li>Authenticate your account and enable features</li>
              <li>Display your public activity (comments, votes, aliases)</li>
              <li>Improve and optimize our platform</li>
              <li>Detect and prevent fraud, abuse, and security issues</li>
              <li>Communicate with you about service updates (if applicable)</li>
            </ul>
          </Section>

          <Section icon={Share2} title="Information Sharing">
            <p>We do not sell your personal information. We may share information:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Publicly:</strong> Your wallet address (or username for email users),
                comments, votes, and aliases are publicly visible on the platform.
              </li>
              <li>
                <strong>Service Providers:</strong> With third-party services that help
                us operate the platform (hosting, analytics).
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to protect
                our rights and safety.
              </li>
            </ul>
          </Section>

          <Section icon={Cookie} title="Cookies & Tracking">
            <p>We use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Keep you signed in</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our platform</li>
              <li>Improve our services</li>
            </ul>
            <p className="mt-3">
              You can control cookies through your browser settings. Note that disabling
              cookies may affect your ability to use certain features.
            </p>
          </Section>

          <Section icon={Globe} title="Third-Party Services">
            <p>Our platform may integrate with third-party services:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Blockchain Data Providers:</strong> We fetch public blockchain
                data from services like Etherscan, CoinGecko, and others.
              </li>
              <li>
                <strong>Wallet Providers:</strong> When you connect your wallet,
                you interact with third-party wallet software (MetaMask, WalletConnect, etc.).
              </li>
              <li>
                <strong>Analytics:</strong> We may use analytics services to understand
                platform usage.
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              These services have their own privacy policies that govern their use of your data.
            </p>
          </Section>

          <Section icon={Settings} title="Data Retention">
            <p>We retain your information:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Account Data:</strong> As long as your account is active.
              </li>
              <li>
                <strong>User Content:</strong> Comments, votes, and aliases remain on
                the platform unless you delete them or your account is terminated.
              </li>
              <li>
                <strong>Log Data:</strong> Typically retained for up to 90 days for
                security and debugging purposes.
              </li>
            </ul>
          </Section>

          <Section icon={UserCheck} title="Your Rights">
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Data portability (receive your data in a structured format)</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us using the information below.
            </p>
          </Section>

          <Section icon={Shield} title="Data Security">
            <p>
              We implement appropriate technical and organizational measures to protect
              your information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Encryption of data in transit (HTTPS)</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments</li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              However, no method of transmission over the Internet is 100% secure.
              We cannot guarantee absolute security of your data.
            </p>
          </Section>

          <Section icon={Mail} title="Contact Us">
            <p>
              If you have questions about this Privacy Policy or want to exercise
              your privacy rights, please contact us at:
            </p>
            <p className="mt-2">
              <a href="mailto:support@coincommons.io" className="text-blue-600 hover:text-blue-800 underline">
                support@coincommons.io
              </a>
            </p>
          </Section>

          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you
              of any material changes by posting the new policy on this page and updating
              the "Last updated" date.
            </p>
            <p className="text-sm text-gray-500 mt-3">
              By continuing to use CoinCommons after changes are posted, you accept
              the updated Privacy Policy.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6 mt-6">
            <p className="text-sm text-gray-600">
              See also: <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">Terms of Service</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
